// File Metadata:
// Filename: chatApi.js
// Version: 1.01
// Created: 2025-06-20
// Last Modified: 2026-02-07
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: RTK Query API slice for chat system - handles REST API fallbacks while preserving Socket.IO real-time functionality. Fixed cache key mismatch for socket events.

import { baseApi } from '../../../../store/api/baseApi';

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all chat rooms
    getChatRooms: builder.query({
      query: () => '/chats/rooms',
      providesTags: ['ChatRooms'],
      keepUnusedDataFor: 300, // 5 minutes cache for chat rooms
      // Transform response to match expected structure
      transformResponse: (response) => {
        return {
          rooms: response.map(room => ({
            ...room,
            lastMessage: room.lastMessage ? {
              ...room.lastMessage,
              senderName: room.lastMessage.senderId?.name || 'Someone',
              senderAvatar: room.lastMessage.senderId?.avatar || null,
              senderId: room.lastMessage.senderId?._id || room.lastMessage.senderId
            } : null
          })),
          unreadCounts: response.reduce((acc, room) => {
            acc[room.roomId] = room.unreadCount || 0;
            return acc;
          }, {})
        };
      },
    }),
    
    // Get messages for a specific room with pagination
    getMessages: builder.query({
      query: ({ roomId, page = 1, limit = 50, since = null }) => ({
        url: `/chats/messages/${roomId}`,
        params: { page, limit, ...(since && { since }) },
      }),
      providesTags: (result, error, { roomId }) => [
        { type: 'Messages', id: roomId },
      ],
      keepUnusedDataFor: 120, // 2 minutes cache for messages
      // Transform response to flatten senderId structure
      transformResponse: (response) => {
        const transformMessages = (messages) => {
          return messages.map(message => ({
            ...message,
            senderName: message.senderId?.name || 'User',
            senderAvatar: message.senderId?.avatar || null,
            senderId: message.senderId?._id || message.senderId
          }));
        };
        
        return {
          ...response,
          messages: response.messages ? transformMessages(response.messages) : [],
          hasMoreMessages: response.pagination ? response.pagination.page < response.pagination.pages : false,
          currentPage: response.pagination ? response.pagination.page : 1,
          totalPages: response.pagination ? response.pagination.pages : 1
        };
      },
    }),
    
    // Load older messages for pagination
    getOlderMessages: builder.query({
      query: ({ roomId, page, limit = 50 }) => ({
        url: `/chats/messages/${roomId}`,
        params: { page, limit },
      }),
      providesTags: (result, error, { roomId }) => [
        { type: 'Messages', id: `${roomId}-page-${result?.currentPage || 1}` },
      ],
      keepUnusedDataFor: 60, // 1 minute cache for older messages
      // Transform response to flatten senderId structure
      transformResponse: (response) => {
        const transformMessages = (messages) => {
          return messages.map(message => ({
            ...message,
            senderName: message.senderId?.name || 'User',
            senderAvatar: message.senderId?.avatar || null,
            senderId: message.senderId?._id || message.senderId
          }));
        };
        
        return {
          ...response,
          messages: response.messages ? transformMessages(response.messages) : [],
          hasMoreMessages: response.pagination ? response.pagination.page < response.pagination.pages : false,
          currentPage: response.pagination ? response.pagination.page : 1,
          totalPages: response.pagination ? response.pagination.pages : 1
        };
      },
    }),
    
    // Send a message (REST API fallback when Socket.IO unavailable)
    sendMessage: builder.mutation({
      query: ({ roomId, content, type = 'text', mediaUrl, mediaMetadata, replyTo, attachmentId }) => ({
        url: '/chats/messages',
        method: 'POST',
        body: { roomId, content, type, mediaUrl, mediaMetadata, replyTo, attachmentId },
      }),
      invalidatesTags: (result, error, { roomId }) => [
        { type: 'Messages', id: roomId },
        'ChatRooms', // Update last message in room list
      ],
      // Optimistic update for immediate UI feedback
      async onQueryStarted({ roomId, content, type, mediaUrl, replyTo }, { dispatch, queryFulfilled, getState }) {
        // Create optimistic message
        const state = getState();
        const currentUser = state.user.data;
        
        if (currentUser) {
          const optimisticMessage = {
            _id: 'temp_' + Date.now(),
            senderId: currentUser.id || currentUser._id,
            senderName: currentUser.name,
            senderAvatar: currentUser.avatar,
            room: roomId,
            content,
            type,
            mediaUrl,
            timestamp: new Date().toISOString(),
            readBy: [currentUser.id || currentUser._id],
            isPending: true,
            replyTo
          };
          
          // Optimistically update the messages cache
          const patchResult = dispatch(
            chatApi.util.updateQueryData('getMessages', { roomId }, (draft) => {
              if (draft.messages) {
                draft.messages.push(optimisticMessage);
              }
            })
          );
          
          try {
            const result = await queryFulfilled;
            // Replace optimistic message with real one
            dispatch(
              chatApi.util.updateQueryData('getMessages', { roomId }, (draft) => {
                if (draft.messages) {
                  const tempIndex = draft.messages.findIndex(msg => msg._id === optimisticMessage._id);
                  if (tempIndex !== -1) {
                    draft.messages[tempIndex] = result.data;
                  }
                }
              })
            );
          } catch {
            // Remove optimistic message on failure
            patchResult.undo();
          }
        }
      },
    }),
    
    // Mark message as read (REST API fallback)
    markMessageRead: builder.mutation({
      query: ({ messageId }) => ({
        url: `/chats/messages/${messageId}/read`,
        method: 'PUT',
      }),
      // Optimistic update
      async onQueryStarted({ messageId, roomId, userId }, { dispatch, queryFulfilled, getState }) {
        const patchResult = dispatch(
          chatApi.util.updateQueryData('getMessages', { roomId }, (draft) => {
            if (draft.messages) {
              const message = draft.messages.find(msg => msg._id === messageId);
              if (message && !message.readBy.includes(userId)) {
                message.readBy.push(userId);
              }
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // React to a message (REST API fallback)
    reactToMessage: builder.mutation({
      query: ({ messageId, emoji, roomId }) => ({
        url: `/chats/messages/${messageId}/reaction`,
        method: 'POST',
        body: { emoji, roomId },
      }),
      // Optimistic update
      async onQueryStarted({ messageId, emoji, roomId }, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        const userId = state.user.data?.id || state.user.data?._id;
        
        if (!userId) return;
        
        const patchResult = dispatch(
          chatApi.util.updateQueryData('getMessages', { roomId }, (draft) => {
            if (draft.messages) {
              const message = draft.messages.find(msg => msg._id === messageId);
              if (message) {
                const reactions = { ...(message.reactions || {}) };
                
                // Toggle reaction
                if (reactions[emoji] && reactions[emoji].includes(userId)) {
                  reactions[emoji] = reactions[emoji].filter(id => id !== userId);
                  if (reactions[emoji].length === 0) {
                    delete reactions[emoji];
                  }
                } else {
                  if (!reactions[emoji]) {
                    reactions[emoji] = [];
                  }
                  reactions[emoji] = [...reactions[emoji], userId];
                }
                
                message.reactions = reactions;
              }
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Edit a message (REST API fallback)
    editMessage: builder.mutation({
      query: ({ messageId, content }) => ({
        url: `/chats/messages/${messageId}`,
        method: 'PUT',
        body: { content },
      }),
      // Optimistic update
      async onQueryStarted({ messageId, content, roomId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          chatApi.util.updateQueryData('getMessages', { roomId }, (draft) => {
            if (draft.messages) {
              const message = draft.messages.find(msg => msg._id === messageId);
              if (message) {
                message.content = content;
                message.edited = true;
              }
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Delete a message (REST API fallback)
    deleteMessage: builder.mutation({
      query: ({ messageId }) => ({
        url: `/chats/messages/${messageId}`,
        method: 'DELETE',
      }),
      // Optimistic update
      async onQueryStarted({ messageId, roomId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          chatApi.util.updateQueryData('getMessages', { roomId }, (draft) => {
            if (draft.messages) {
              const message = draft.messages.find(msg => msg._id === messageId);
              if (message) {
                message.deleted = true;
                message.content = '';
                message.edited = false;
              }
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Create a group chat
    createGroupChat: builder.mutation({
      query: ({ name, participantIds }) => ({
        url: '/chats/rooms/group',
        method: 'POST',
        body: { name, participantIds },
      }),
      invalidatesTags: ['ChatRooms'],
    }),
    
    // Create a private chat
    createPrivateChat: builder.mutation({
      query: ({ userId }) => ({
        url: '/chats/rooms/private',
        method: 'POST',
        body: { participantId: userId },
      }),
      invalidatesTags: ['ChatRooms'],
    }),
    
    // Update a group chat
    updateGroupChat: builder.mutation({
      query: ({ roomId, name, participantIds }) => ({
        url: `/chats/rooms/group/${roomId}`,
        method: 'PUT',
        body: { name, participantIds },
      }),
      invalidatesTags: ['ChatRooms'],
      // Optimistic update
      async onQueryStarted({ roomId, name, participantIds }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          chatApi.util.updateQueryData('getChatRooms', undefined, (draft) => {
            const room = draft.rooms.find(r => r.roomId === roomId);
            if (room) {
              room.name = name;
              room.participants = participantIds;
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    
    // Get or create private chat
    getPrivateChat: builder.mutation({
      query: (participantId) => ({
        url: '/chats/rooms/private',
        method: 'POST',
        body: { participantId },
      }),
      invalidatesTags: ['ChatRooms'],
    }),
    
    // Sync offline messages
    syncOfflineMessages: builder.mutation({
      query: (offlineMessages) => ({
        url: '/chats/messages/sync',
        method: 'POST',
        body: { messages: offlineMessages },
      }),
      invalidatesTags: ['ChatRooms'],
      // This will be called from the thunk after processing offline queue
    }),
    
    // Get chat room details
    getChatRoom: builder.query({
      query: (roomId) => `/chats/rooms/${roomId}`,
      providesTags: (result, error, roomId) => [{ type: 'ChatRoom', id: roomId }],
    }),
    
    // Update user typing status (REST fallback)
    updateTypingStatus: builder.mutation({
      query: ({ roomId, isTyping }) => ({
        url: `/chats/rooms/${roomId}/typing`,
        method: 'POST',
        body: { isTyping },
      }),
      // No cache updates needed as this is handled by socket events
    }),
    
    // Get chat room participants
    getRoomParticipants: builder.query({
      query: (roomId) => `/chats/rooms/${roomId}/participants`,
      providesTags: (result, error, roomId) => [{ type: 'RoomParticipants', id: roomId }],
    }),
    
    // Add participant to room (group chats only)
    addParticipant: builder.mutation({
      query: ({ roomId, participantId }) => ({
        url: `/chats/rooms/${roomId}/participants`,
        method: 'POST',
        body: { participantId },
      }),
      invalidatesTags: (result, error, { roomId }) => [
        { type: 'RoomParticipants', id: roomId },
        'ChatRooms',
      ],
    }),
    
    // Remove participant from room (group chats only)
    removeParticipant: builder.mutation({
      query: ({ roomId, participantId }) => ({
        url: `/chats/rooms/${roomId}/participants/${participantId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roomId }) => [
        { type: 'RoomParticipants', id: roomId },
        'ChatRooms',
      ],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetChatRoomsQuery,
  useGetMessagesQuery,
  useGetOlderMessagesQuery,
  useSendMessageMutation,
  useMarkMessageReadMutation,
  useReactToMessageMutation,
  useEditMessageMutation,
  useDeleteMessageMutation,
  useCreateGroupChatMutation,
  useCreatePrivateChatMutation,
  useUpdateGroupChatMutation,
  useGetPrivateChatMutation,
  useSyncOfflineMessagesMutation,
  useGetChatRoomQuery,
  useUpdateTypingStatusMutation,
  useGetRoomParticipantsQuery,
  useAddParticipantMutation,
  useRemoveParticipantMutation,
} = chatApi;

// Helper to update all cached getMessages queries for a given roomId.
// RTK Query uses the full argument object as the cache key, so we must
// iterate over every cached entry whose roomId matches.
const forEachMessagesCacheEntry = (roomId, dispatch, updater) => {
  // Get all cached query keys for 'getMessages'
  // Use the state to find entries that match our roomId
  dispatch((dispatchFn, getState) => {
    const state = getState();
    const queryCacheEntries = state[baseApi.reducerPath]?.queries || {};
    
    // Find all getMessages cache entries for this roomId
    Object.keys(queryCacheEntries).forEach((cacheKey) => {
      // Cache keys for getMessages look like: getMessages({"roomId":"...","page":1,"limit":50,...})
      if (cacheKey.startsWith('getMessages(') && cacheKey.includes(roomId)) {
        // Extract the original args from the cache key
        try {
          const argsStr = cacheKey.slice('getMessages('.length, -1);
          const args = JSON.parse(argsStr);
          if (args.roomId === roomId) {
            dispatchFn(
              chatApi.util.updateQueryData('getMessages', args, updater)
            );
          }
        } catch {
          // If parsing fails, skip this entry
        }
      }
    });
  });
};

// Export utilities for manual cache updates from socket events
export const updateCacheOnSocketEvent = {
  // Update cache when receiving a new message via socket
  receiveMessage: (message, dispatch) => {
    forEachMessagesCacheEntry(message.room, dispatch, (draft) => {
      if (draft.messages) {
        const existingIndex = draft.messages.findIndex(msg => msg._id === message._id);
        if (existingIndex !== -1) {
          // Update existing message
          draft.messages[existingIndex] = { ...draft.messages[existingIndex], ...message };
        } else {
          // Check for optimistic message to replace
          const optimisticIndex = draft.messages.findIndex(msg => 
            msg.isOptimistic && 
            msg.content === message.content && 
            String(msg.senderId?._id || msg.senderId) === String(message.senderId?._id || message.senderId) &&
            msg.room === message.room
          );
          
          if (optimisticIndex !== -1) {
            // Replace optimistic message with real message
            draft.messages[optimisticIndex] = message;
          } else {
            // Add new message
            draft.messages.push(message);
          }
        }
      }
    });
    
    // Update room's last message
    dispatch(
      chatApi.util.updateQueryData('getChatRooms', undefined, (draft) => {
        if (draft?.rooms) {
          const room = draft.rooms.find(r => r.roomId === message.room);
          if (room) {
            room.lastMessage = message;
          }
        }
      })
    );
  },
  
  // Update cache when message read status changes via socket
  updateReadStatus: (messageId, roomId, userId, dispatch) => {
    forEachMessagesCacheEntry(roomId, dispatch, (draft) => {
      if (draft.messages) {
        const message = draft.messages.find(msg => msg._id === messageId);
        if (message && !message.readBy.includes(userId)) {
          message.readBy.push(userId);
        }
      }
    });
  },
  
  // Update cache when message is edited via socket
  editMessage: (updatedMessage, dispatch) => {
    forEachMessagesCacheEntry(updatedMessage.room, dispatch, (draft) => {
      if (draft.messages) {
        const messageIndex = draft.messages.findIndex(msg => msg._id === updatedMessage._id);
        if (messageIndex !== -1) {
          draft.messages[messageIndex] = { ...draft.messages[messageIndex], ...updatedMessage };
        }
      }
    });
  },
  
  // Update cache when message is deleted via socket
  deleteMessage: (messageId, roomId, dispatch) => {
    forEachMessagesCacheEntry(roomId, dispatch, (draft) => {
      if (draft.messages) {
        const message = draft.messages.find(msg => msg._id === messageId);
        if (message) {
          message.deleted = true;
          message.content = '';
          message.edited = false;
        }
      }
    });
  },
  
  // Update cache when room is updated via socket
  updateRoom: (updatedRoom, dispatch) => {
    dispatch(
      chatApi.util.updateQueryData('getChatRooms', undefined, (draft) => {
        const roomIndex = draft.rooms.findIndex(r => r.roomId === updatedRoom.roomId);
        if (roomIndex !== -1) {
          draft.rooms[roomIndex] = { ...draft.rooms[roomIndex], ...updatedRoom };
        } else {
          draft.rooms.push(updatedRoom);
        }
      })
    );
  },
  
  // Invalidate cache when needed
  invalidateMessages: (roomId, dispatch) => {
    dispatch(chatApi.util.invalidateTags([{ type: 'Messages', id: roomId }]));
  },
  
  invalidateRooms: (dispatch) => {
    dispatch(chatApi.util.invalidateTags(['ChatRooms']));
  }
};
